<template>
  <f7-page>
    <AppLayout>
      <template #main>
    <f7-block-title>{{$t('common.admins')}}</f7-block-title>
    <f7-block class="member-list margin-horizontal">
      <MemberCard
        v-for="m in admins" :key="m.userId"
        :member="m"
        :can-manage="viewerCanManage && m.role !== 'OWNER'"
        :row-actionable="viewerCanManage && m.role !== 'OWNER'"
        :show-nudge="false"
        @open-menu="openMenu"
        @open-actions="openMenu"
      />
      <div v-if="!admins.length && !loading" class="empty-hint">{{$t('common.noAdmins')}}</div>
    </f7-block>

    <f7-block-title>{{$t('common.members')}}</f7-block-title>
    <f7-block class="member-list margin-horizontal">
      <MemberCard
        v-for="m in regularMembers" :key="m.userId"
        :member="m"
        :can-manage="viewerCanManage"
        :row-actionable="viewerCanManage"
        :show-nudge="overdueUserIds.has(m.userId)"
        @open-menu="openMenu"
        @open-actions="openMenu"
        @nudge="handleNudge"
      />
      <div v-if="!regularMembers.length && !loading" class="empty-hint">{{$t('common.noMembers')}}</div>
    </f7-block>

    <template v-if="guests.length">
      <f7-block-title>{{$t('common.guests')}}</f7-block-title>
      <f7-block class="member-list margin-horizontal">
        <MemberCard
          v-for="m in guests" :key="m.userId"
          :member="m"
          :can-manage="viewerCanManage"
          :row-actionable="viewerCanManage"
          @open-menu="openMenu"
          @open-actions="openMenu"
        />
      </f7-block>
    </template>

    <SkeletonList v-if="loading" variant="row" :rows="5" class="loading-block" />

    <f7-block class="member-footer" style=" display: flex;
  justify-content: center;">
      <f7-button large round raised fill @click="$emit('open-full-page')">
        {{$t('common.manageMembersAndInvitations')}}
      </f7-button>
    </f7-block>

    <RoleChangeSheet
      ref="roleChangeSheetRef"
      @change-role="handleRoleChange"
      @remove="handleRemove"
    />
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import AppLayout from '../common/AppLayout.vue'
import SkeletonList from '../common/SkeletonList.vue'
import MemberCard from './MemberCard.vue'
import RoleChangeSheet from './RoleChangeSheet.vue'
import { useMembersStore } from '../../js/pinia-stores/members.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import i18n from '../../js/i18n.js'
const { t } = i18n.global
const props = defineProps({ groupId: { type: String, required: true } })
defineEmits(['open-full-page'])

const membersStore = useMembersStore()
const keycloakStore = useKeycloakStore()

const selectedMember = ref(null)
const roleChangeSheetRef = ref(null)
const loading = ref(true)
const overdueUserIds = ref(new Set())

const admins = computed(() =>
  membersStore.members.filter(m => m.role === 'OWNER' || m.role === 'ADMIN'))
const regularMembers = computed(() =>
  membersStore.members.filter(m => m.role === 'MEMBER'))
const guests = computed(() =>
  membersStore.members.filter(m => m.role === 'GUEST'))

const viewerCanManage = computed(() => {
  const viewer = membersStore.members.find(m => m.userId === keycloakStore.subject)
  return viewer?.role === 'OWNER' || viewer?.role === 'ADMIN'
})

onMounted(async () => {
  await membersStore.fetchMembers(props.groupId)
  loading.value = false
})

function openMenu(member, targetEl) {
  selectedMember.value = member
  roleChangeSheetRef.value?.openRoleChangeActionsPopover(member, targetEl)
}

async function handleRoleChange(newRole) {
  try {
    await membersStore.changeRole(props.groupId, selectedMember.value.userId, newRole)
    f7.toast.show({ text:t('group.role_updated'),  closeTimeout: 1500 })
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message ||t('group.role_update_failed') )
  }
}

async function handleRemove() {
  f7.dialog.confirm(
    t('group.member_remove_confirm', { memberName: selectedMember.value.name }),
    async () => {
      await membersStore.removeMember(props.groupId, selectedMember.value.userId)
      f7.toast.show({ text: t('group.member_removed'), closeTimeout: 1500 })
    }
  )
}

async function handleNudge(member) {
  await membersStore.nudge(props.groupId, member.userId)
  f7.toast.show({ text: t('group.nudge_sent', { memberName: member.name }), closeTimeout: 1500 })
}
</script>

<style scoped>
.member-list { background: var(--f7-surface-container, #f5f5f5); border-radius: 10px; padding: 0 8px; }
.empty-hint { font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); padding: 12px 4px; text-align: center; }
.loading-block { text-align: center; padding: 20px 0; }
.member-footer { padding-top: 4px; }
</style>
